import React from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from "../../ui/chart";
import { CHART_MARGIN_TOP } from "../chartStyles";
import { addSpacesAroundSlashes, formatNumber } from "../utils";
import { InsightLegendContent } from "./InsightLegendContent";
import { CardNotes } from "../CardNotes";

interface CategoryCount {
  key: string;
  displayName: string;
  value: number;
}

interface HorizontalBarChartProps {
  title: string;
  description?: string;
  notes?: string[];
  focusData: CategoryCount[];
  comparisonData: CategoryCount[];
  focusYear: number | string;
  comparisonYear: number | string;
  // What each period is called when that isn't its year, like "Past 6 Months"
  focusLabel?: string;
  comparisonLabel?: string;
  headerAction?: React.ReactNode;
}

export function HorizontalBarChart({
  title,
  description,
  notes,
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  focusLabel = String(focusYear),
  comparisonLabel = String(comparisonYear),
  headerAction,
}: HorizontalBarChartProps) {
  const chartData = focusData.map((focusEntry) => {
    const comparisonEntry = comparisonData.find(
      (compEntry) => compEntry.displayName === focusEntry.displayName,
    );
    return {
      displayName: focusEntry.displayName,
      [focusYear]: focusEntry.value,
      [comparisonYear]: comparisonEntry ? comparisonEntry.value : 0,
    };
  });

  const chartConfig = {
    [focusYear]: {
      label: focusLabel,
      color: "var(--chart-1)",
    },
    [comparisonYear]: {
      label: comparisonLabel,
      color: "var(--chart-2)",
    },
  };

  const chartHeight = Math.max(250, chartData.length * 50);

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description || `Comparison for ${focusLabel} and ${comparisonLabel}`}
        </CardDescription>
        <CardNotes notes={notes} />
        {headerAction && <CardAction>{headerAction}</CardAction>}
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={chartConfig}
          className="tw:w-full"
          style={{ height: `${chartHeight}px` }}
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{
              left: 20,
              right: CHART_MARGIN_TOP,
            }}
          >
            <CartesianGrid horizontal={false} />
            <YAxis
              dataKey="displayName"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              width={100}
              tickFormatter={addSpacesAroundSlashes}
            />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
            <Bar
              dataKey={String(focusYear)}
              fill={`var(--color-${String(focusYear)})`}
              radius={4}
            />
            <Bar
              dataKey={String(comparisonYear)}
              fill={`var(--color-${String(comparisonYear)})`}
              radius={4}
            />
            <ChartLegend content={<InsightLegendContent />} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
